<template>
  <div class="flex h-full flex-col">
    <div class="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
      <div>
        <SelectEmailFromAddress v-model:from="localModelNotification.from" />
      </div>
      <div v-if="isFuneralHomeSender">
        <BaseCheckBox
          v-model="localModelNotification.send_copy_to_agency"
          :label="$t('global.send_copy_to_agency')"
          name="send_copy_to_agency"
          edition-mode
        />
      </div>

      <div class="flex flex-row items-baseline gap-2">
        <div class="flex-1">
          <SelectEmailable
            v-model:emailables="localModelNotification.to"
            :label="$t('global.email_to')"
            :allow-tags="true"
            name="email_to"
            edition-mode
            required
          />
        </div>
        <div>
          <div class="squircle-md flex flex-row gap-0.5 rounded border p-0.5">
            <button
              class="squircle-md disabled:bg-primary-200 rounded border px-3 py-1.5 font-bold disabled:cursor-not-allowed disabled:text-slate-600"
              :class="{
                'bg-primary text-contrast-primary': showCC
              }"
              :disabled="
                Array.isArray(modelNotification.cc) &&
                modelNotification.cc.length > 0 &&
                showCC
              "
              @click="showCC = !showCC"
            >
              {{ $t('global.email_cc') }}
            </button>
            <button
              class="squircle-md disabled:bg-primary-200 rounded border px-3 py-1.5 font-bold disabled:cursor-not-allowed disabled:text-slate-600"
              :class="{
                'bg-primary text-contrast-primary': showBCC
              }"
              :disabled="
                Array.isArray(modelNotification.bcc) &&
                modelNotification.bcc.length > 0 &&
                showBCC
              "
              @click="showBCC = !showBCC"
            >
              {{ $t('global.email_bcc') }}
            </button>
          </div>
        </div>
      </div>
      <div v-if="showCC">
        <SelectEmailable
          v-model:emailables="localModelNotification.cc"
          :label="$t('global.email_cc')"
          :allow-tags="true"
          name="email_cc"
          edition-mode
          required
        />
      </div>
      <div v-if="showBCC">
        <SelectEmailable
          v-model:emailables="localModelNotification.bcc"
          :label="$t('global.email_bcc')"
          :allow-tags="true"
          name="email_bcc"
          edition-mode
          required
        />
      </div>
      <div>
        <BaseShowEditInput
          v-model="localModelNotification.subject"
          :label="$t('attributes.email_subject')"
          name="subject"
          :errors="errors?.subject"
          edition-mode
          required
        />
      </div>
      <div>
        <BaseWysiwygMail
          v-model:message="localModelNotification.message"
          v-model:to="localModelNotification.to"
          :signature="signature"
          :signature-loading="signatureLoading"
          :from="localModelNotification.from"
          :label="$t('global.message')"
          :value="modelNotification.message"
          :model-type="modelType"
          :model-id="modelId"
          name="message"
          :errors="errors?.message"
          edition-mode
          required
          @update:signature="onSignatureChosen"
          @update:message="handleMessageTemplate"
        />
        <ErrorContainer error-key="message" :errors="errors" />
        <ConfirmModal ref="confirmRecipient" />
      </div>
      <slot name="extra-content" />
      <div class="flex flex-col gap-2">
        <h3 class="text-base font-bold">
          {{ $t('global.attached_files') }}
        </h3>
        <div class="flex flex-wrap gap-2">
          <AttachmentComponent
            v-for="attachment in localModelNotification.email_attachments"
            :key="attachment.id"
            :attachment="attachment"
            linked-to-notification
            can-delete-attachment
          />
          <AttachmentComponent
            v-for="attachment in localModelNotification.attachables"
            :key="attachment.id"
            :attachment="attachment"
            linked-to-notification
            @remove-attachment="
              () => removeAttachment(attachment.type, attachment.id)
            "
          />
          <LocalFileAttachmentComponent
            v-for="(file, idx) in localModelNotification.files"
            :key="idx"
            :attachment="file.file"
            @remove-attachment="() => removeFile(idx)"
          />
          <label
            class="squircle-md flex w-56 flex-row items-center justify-center rounded border-2 border-dashed p-2"
          >
            <FontAwesomeIcon icon="plus" size="xl" />
            <input type="file" class="hidden" @change="addLocalFile" />
          </label>
        </div>
      </div>
    </div>
    <div class="flex shrink-0 flex-row justify-end gap-2">
      <BaseButton
        :title="$t('button.reset')"
        theme-color="bg-secondary text-contrast-secondary"
        icon="times"
        inverse-icon-order
        :disabled="sending"
        :display-loader="sending"
        @click="$emit('reset')"
      />
      <BaseButton
        :title="$t('button.close')"
        theme-color="bg-secondary text-contrast-secondary"
        icon="times"
        inverse-icon-order
        :disabled="sending"
        :display-loader="sending"
        @click="$emit('close')"
      />
      <BaseButton
        :title="$t('button.save_to_draft')"
        icon="floppy-disk"
        icon-type="fal"
        :disabled="sending"
        :display-loader="sending"
        @click="saveNewOrExistingEmailToDraft"
      />
      <BaseButton
        :title="$t('button.send')"
        icon="paper-plane"
        :disabled="sending"
        :display-loader="sending"
        @click="sendNewEmail"
      />
    </div>
  </div>
</template>

<script>
import SelectEmailFromAddress from '@c/Mail/component/SelectEmailFromAddress.vue'
import SelectEmailable from '@c/SelectAjaxComponent/SelectEmailable.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import ErrorContainer from '@c/addf-package/components/BaseShowEditInput/ErrorContainer.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseWysiwygMail from '@c/Mail/component/BaseWysiwygMail.vue'
import AttachmentComponent from '@/components/Mail/component/AttachmentComponent.vue'
import { mapActions, mapGetters } from 'vuex'
import LocalFileAttachmentComponent from '@c/Mail/component/LocalFileAttachmentComponent.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import BaseCheckBox from '@c/addf-package/components/BaseCheckBox/BaseCheckBox.vue'

export default {
  name: 'ComposeForm',
  components: {
    BaseCheckBox,
    ConfirmModal,
    LocalFileAttachmentComponent,
    AttachmentComponent,
    BaseWysiwygMail,
    FontAwesomeIcon,
    BaseButton,
    ErrorContainer,
    BaseShowEditInput,
    SelectEmailable,
    SelectEmailFromAddress
  },
  props: {
    modelNotification: {
      type: Object,
      required: true
    },
    modelType: {
      type: String,
      required: false,
      default: null
    },
    modelId: {
      type: String,
      required: false,
      default: null
    },
    suggestedContacts: {
      type: Array,
      required: false,
      default: () => []
    },
    errors: {
      type: Object,
      required: false,
      default: null
    },
    sendRecord: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: [
    'update:modelNotification',
    'close',
    'saved',
    'sended',
    'reset',
    'add-attachment'
  ],
  data() {
    return {
      sending: false,
      signature: null,
      signatureManuallyChosen: false,
      signatureFromDocument: false,
      signatureInitialized: false,
      signatureAbortController: null,
      signatureLoading: false,
      message: '',
      showCC: false,
      showBCC: false
    }
  },
  computed: {
    ...mapGetters({
      auth: 'auth/getAuth'
    }),
    localModelNotification: {
      get() {
        return this.modelNotification
      },
      set(value) {
        this.$emit('update:modelNotification', value)
      }
    },
    isFuneralHomeSender() {
      return this.localModelNotification.from?.type === 'App\\Models\\FuneralHome'
    }
  },
  watch: {
    'localModelNotification.cc': {
      handler(newVal) {
        if (!this.showCC && Array.isArray(newVal) && newVal.length > 0) {
          this.showCC = true
        }
      },
      deep: true
    },
    'localModelNotification.bcc': {
      handler(newVal) {
        if (!this.showBCC && Array.isArray(newVal) && newVal.length > 0) {
          this.showBCC = true
        }
      },
      deep: true
    },
    'localModelNotification.from': {
      handler(newValue, oldValue) {
        if (!newValue?.id) return

        // First time the sender lands (fetch resolved): decide the initial
        // signature once, after the document-signature extraction has run.
        if (!this.signatureInitialized) {
          this.signatureInitialized = true
          this.$nextTick(() => this.initSignature())
          return
        }

        // Later, a genuine sender switch follows the new sender.
        if (newValue.id !== oldValue?.id) {
          this.signatureFromDocument = false
          this.signatureManuallyChosen = false
          this.autofillSignatureFromSender(newValue)
        }
      }
    },
    'localModelNotification.message': {
      handler(newValue) {
        this.extractDocumentSignature(newValue)
      }
    }
  },
  methods: {
    ...mapActions({
      processAddOrUpdateModelNotificationAction:
        'modelNotification/newAddOrUpdateModelNotification',
      processFetchSignatureFormatedAction: 'user/fetchSignatureFormated'
    }),
    async autofillSignatureFromSender(fromValue) {
      // Manual quill pick wins; no sender; or body already holds a backend signature.
      if (this.signatureManuallyChosen || !fromValue) return
      if ((this.localModelNotification.message || '').includes('data-signature')) return

      const senderType =
        fromValue.type === 'App\\Models\\FuneralHome' ? 'funeralHome' : 'user'

      // Cancel any in-flight signature request so only the latest sender wins.
      if (this.signatureAbortController) {
        this.signatureAbortController.abort()
      }

      const abortController = new AbortController()
      this.signatureAbortController = abortController
      this.signatureLoading = true

      try {
        const response = await this.processFetchSignatureFormatedAction({
          id: this.auth.id,
          model_type: this.modelType,
          model_id: this.modelId,
          type: senderType,
          from: fromValue.id,
          signal: abortController.signal
        })

        this.signature = response?.text ?? null
      } catch (error) {
        // Aborted by a newer request: keep the current slot silently.
        if (abortController.signal.aborted) return
        this.$h.toastError(this.$t('toasts.something_went_wrong'))
      } finally {
        // Only the latest request owns the loading state.
        if (this.signatureAbortController === abortController) {
          this.signatureLoading = false
        }
      }
    },
    onSignatureChosen(signatureHtml) {
      this.signature = signatureHtml
      this.signatureManuallyChosen = true
    },
    initSignature() {
      // The document-configured signature (extracted into the slot) wins on load.
      if (this.signatureManuallyChosen || this.signatureFromDocument) return
      this.autofillSignatureFromSender(this.localModelNotification.from)
    },
    extractDocumentSignature(message) {
      if (!message || !message.includes('data-signature')) return

      const wrapper = document.createElement('div')
      wrapper.innerHTML = message
      const signatureNode = wrapper.querySelector('[data-signature="true"]')
      if (!signatureNode) return

      // The document configures its own signature in the body: move it into the
      // slot (single source, priority over the sender), and strip it from the body.
      this.signature = signatureNode.innerHTML
      this.signatureFromDocument = true
      signatureNode.remove()
      this.localModelNotification.message = wrapper.innerHTML
    },
    comingSoon() {
      this.$h.toastWarning(this.$t('global.available_soon'))
    },
    removeAttachment(type, id) {
      this.localModelNotification.attachables =
        this.localModelNotification.attachables.filter(
          (attachment) => attachment.id !== id && attachment.type === type
        )
    },
    removeFile(index) {
      this.localModelNotification.files.splice(index, 1)
    },
    async handleMessageTemplate(payload) {
      if (typeof payload === 'string') {
        return
      }

      if (payload.attachments?.length) {
        for (let attachment of payload.attachments) {
          this.$emit('add-attachment', attachment)
        }
      }

      const templateTo = payload.to || []
      const templateMailSubject = payload.mail_subject || ''
      const currentTo = this.localModelNotification.to || []

      this.localModelNotification.message = payload.text || ''
      if (!this.localModelNotification.subject) {
        this.localModelNotification.subject = templateMailSubject
      }

      if (!templateTo.length) return

      if (!currentTo.length) {
        this.localModelNotification.to = templateTo
        return
      }

      const templateEmail = templateTo[0]?.emailable
      const currentEmail = currentTo[0]?.emailable

      if (templateEmail === currentEmail) return

      const confirmed = await this.$refs.confirmRecipient.show({
        title: this.$t('Conflit de destinataire'),
        message: this.$t('global.valid_recipient_email', {
          templateEmail: templateTo[0]?.name,
          currentEmail: currentTo[0]?.name
        }),
        okButton: this.$t('global.message_recipient'),
        cancelButton: this.$t('global.current_recipient'),
        showHtmlTags: true
      })

      if (confirmed) {
        this.localModelNotification.to = templateTo
      }
    },
    addLocalFile(event) {
      if (this.localModelNotification) {
        if (!this.localModelNotification['files']) {
          this.localModelNotification['files'] = []
        }
        const files = Array.from(event.target.files || [])
        this.localModelNotification.files.push(
          ...files.map((file) => ({ file, properties: null }))
        )
      }
      event.target.value = ''
    },
    saveNewOrExistingEmailToDraft() {
      this.sending = true
      this.localModelNotification.state = 7
      this.localModelNotification.priority = 'false'
      this.localModelNotification.message =
        (this.localModelNotification.message || '') + (this.signature || '')

      if (this.localModelNotification.id) {
        this.processAddOrUpdateModelNotificationAction(
          this.localModelNotification
        )
          .then((result) => {
            this.$h.toastSuccess(
              this.$t('toasts.message_has_been_saved_to_draft')
            )
          })
          .finally((_) => {
            this.sending = false
          })
      } else {
        this.processAddOrUpdateModelNotificationAction(
          this.localModelNotification
        )
          .then((result) => {
            this.$h.toastSuccess(
              this.$t('toasts.message_has_been_saved_to_draft')
            )
            this.$emit('saved')
          })
          .finally((_) => {
            this.sending = false
          })
      }
    },
    sendNewEmail() {
      this.sending = true

      this.localModelNotification.state = 0
      this.localModelNotification.priority = 'false'
      this.localModelNotification.message =
        (this.localModelNotification.message || '') + (this.signature || '')

      if (!this.localModelNotification.attachments) {
        this.localModelNotification.attachments = []
      }

      console.log(this.sendRecord)

      this.processAddOrUpdateModelNotificationAction(
        // this.localModelNotification
        {
          ...this.localModelNotification,
          sendRecord: this.sendRecord
        }
      )
        .then((result) => {
          this.$h.toastSuccess(this.$t('toasts.email_sent'))
          this.$emit('sended')
        })
        .finally((_) => {
          this.sending = false
        })
    }
  }
}
</script>
